/**
 * Body of the Candid New Document Upload modal.
 *
 * Owns the document-name field and drag-drop uploader, then emits a
 * {@link CandidDocumentUploadFormState} snapshot to the parent whenever
 * the name, GCS path, or validity changes.
 *
 * @module newDocUploadBody
 */
import { InputField } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { CandidDocumentUploadFormState } from "../../../data-model/candidDocumentUploadFormState";
import DragDropUpload from "../../dragDropUpload/dragDropUpload";

const MAX_FILE_SIZE_BYTES = 30 * 1024 * 1024;

/** Controlled props for {@link NewDocUploadBody}. */
interface NewDocUploadBodyProps {
  /**
   * Receives the latest form snapshot for Save-button gating and the POST body.
   *
   * @param state - Current doc name, GCS path, and validity
   */
  onChange: (state: CandidDocumentUploadFormState) => void;
}

/**
 * Returns the lowercased extension of a file name, or an empty string.
 *
 * @param fileName - Original file name (e.g. `letter.pdf`)
 * @returns Extension without the leading dot
 */
const getFileExtension = (fileName: string): string => {
  const parts = fileName.split(".");
  if (parts.length < 2) {
    return "";
  }
  return (parts.pop() ?? "").toLowerCase();
};

/**
 * Renders the document name input and file dropzone, emitting form state upward.
 * A helper banner reminds users to upload only the authorization approval document.
 *
 * @param props.onChange - Parent setter for {@link CandidDocumentUploadFormState}
 * @returns The document upload form body
 */
const NewDocUploadBody = ({ onChange }: NewDocUploadBodyProps) => {
  const [docName, setDocName] = useState("");
  const [uploadedFilePath, setUploadedFilePath] = useState("");
  const [uploadedFileCount, setUploadedFileCount] = useState(0);
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();

  const filePathPrefix = useMemo(() => {
    const uuid = uuidv4();
    return `/medical_pa_orders/${generalOrderDetails?.order?.order_id}/candid_auth_upload_documents/${uuid}`;
  }, [generalOrderDetails]);

  /**
   * Builds the GCS object path for a dropped file by appending its extension.
   *
   * @param file - Selected file (`name` supplies the extension)
   * @returns Path under `candid_auth_upload_documents` for the current order
   */
  const makePathForFile = useCallback(
    (file: { name: string }): string => {
      const extension = getFileExtension(file.name);
      return extension ? `${filePathPrefix}.${extension}` : filePathPrefix;
    },
    [filePathPrefix],
  );

  useEffect(() => {
    const trimmedName = docName.trim();
    const isValid =
      trimmedName.length > 0 &&
      uploadedFileCount > 0 &&
      uploadedFilePath.trim().length > 0;

    onChange({
      docName: trimmedName,
      filePath: uploadedFilePath,
      isValid,
    });
  }, [docName, uploadedFileCount, uploadedFilePath, onChange]);

  return (
    <div className="new-doc-upload-body--container flex w-full flex-col gap-3">
      <div className="flex flex-col gap-2 rounded-md bg-secondaryYellow-11 p-2">
        <div className="text-xs font-medium text-primaryGray-4">
          Please upload the authorisation approval document only
        </div>
      </div>
      <div className="doc-name--input">
        <InputField
          label="Document Name"
          placeholder="Enter Custom File Name"
          value={docName}
          onChange={(e) => setDocName(e.target.value)}
          fullWidth
          required
          size="md"
        />
      </div>
      <div className="doc-name--upload">
        <DragDropUpload
          id="new-doc-upload"
          filePath={filePathPrefix}
          getFilePath={makePathForFile}
          allowedFileTypes={["pdf", "jpg", "jpeg", "png"]}
          maxFileSize={MAX_FILE_SIZE_BYTES}
          fileListLength={setUploadedFileCount}
          fileDetails={(files) => {
            const file = files[0];
            setUploadedFilePath(file ? makePathForFile(file) : "");
          }}
          onDeleteFile={() => {
            setUploadedFilePath("");
            setUploadedFileCount(0);
          }}
        />
      </div>
    </div>
  );
};

export default NewDocUploadBody;
